import React, { createContext, useContext, useEffect, useRef } from "react";
import { useDevice } from "@/components/Device";

const Ctx = createContext(null);

export function EditableProvider({ blockId, elements = {}, editor = false, selected = null, onSelect, onChange, register, onGuides, scale = 1, children }) {
  return <Ctx.Provider value={{ blockId, elements, editor, selected, onSelect, onChange, register, onGuides, scale }}>{children}</Ctx.Provider>;
}

const HANDLES = [["nw", "-top-1 -left-1 cursor-nwse-resize"], ["n", "-top-1 left-1/2 -translate-x-1/2 cursor-ns-resize"], ["ne", "-top-1 -right-1 cursor-nesw-resize"], ["e", "top-1/2 -right-1 -translate-y-1/2 cursor-ew-resize"], ["se", "-bottom-1 -right-1 cursor-nwse-resize"], ["s", "-bottom-1 left-1/2 -translate-x-1/2 cursor-ns-resize"], ["sw", "-bottom-1 -left-1 cursor-nesw-resize"], ["w", "top-1/2 -left-1 -translate-y-1/2 cursor-ew-resize"]];

// Wraps any sub-element of a block so it can be selected, moved, resized and edited in the layout editor.
export function E({ id, label, kind = "box", children, className = "", display = "inline-block", style: extra = {}, defaultVisibility }) {
  const ctx = useContext(Ctx);
  const device = useDevice();
  const ref = useRef(null);
  const drag = useRef(null);
  const el = ctx?.elements?.[id] || {};

  useEffect(() => { ctx?.register?.(id, label || id, kind, defaultVisibility); }, [ctx?.blockId, id, ctx?.register]); // eslint-disable-line

  const visible = !el.hidden && (el.visibility?.[device] ?? defaultVisibility?.[device] ?? true);
  const [fullW, setFullW] = React.useState(null);
  React.useLayoutEffect(() => {
    if (!el.full || !ref.current) { setFullW(null); return; }
    const calc = () => {
      const n = ref.current; if (!n) return;
      const root = n.closest("[data-fullw-root]") || document.documentElement;
      const rr = root.getBoundingClientRect(); const z = (n.getBoundingClientRect().width / (n.offsetWidth || 1)) || 1;
      const prev = n.style.marginLeft; n.style.marginLeft = "0px"; n.style.width = "auto";
      const pr = n.getBoundingClientRect();
      n.style.marginLeft = prev;
      setFullW({ w: rr.width / z, ml: (rr.left - pr.left) / z });
    };
    calc(); window.addEventListener("resize", calc); return () => window.removeEventListener("resize", calc);
  }, [el.full, el.x, el.w, device]); // eslint-disable-line
  if (!visible) return null;
  const content = typeof children === "function" ? children(el) : children;
  if (!ctx) return content;
  const editor = ctx.editor;
  const isSel = editor && ctx.selected === id;

  const scale = el.scale || 1;
  const f = ctx.scale || 1; // responsive factor: authored px → current viewport px
  const px = (v) => (v ? Math.round(v * f) : 0);
  const style = {
    ...extra,
    display: el.abs || el.full ? "block" : display,
    position: el.abs || /\babsolute\b/.test(className) ? "absolute" : "relative",
    left: el.abs ? px(el.x) + "px" : undefined,
    top: el.abs ? px(el.y) + "px" : undefined,
    transform: !el.abs && (el.x || el.y || scale !== 1 || el.rot) ? `translate(${px(el.x)}px, ${px(el.y)}px) rotate(${el.rot || 0}deg) scale(${scale})` : el.abs && (scale !== 1 || el.rot) ? `rotate(${el.rot || 0}deg) scale(${scale})` : undefined,
    transformOrigin: "center",
    width: el.full && fullW ? fullW.w + "px" : el.w ? px(el.w) + "px" : undefined,
    marginLeft: el.full && fullW ? fullW.ml + "px" : undefined,
    maxWidth: el.full ? "none" : undefined,
    height: el.h ? px(el.h) + "px" : undefined,
    zIndex: el.z || (isSel ? 30 : undefined),
    opacity: el.opacity != null ? el.opacity : undefined,
    fontSize: el.fontSize ? el.fontSize + "px" : undefined,
    color: el.color || undefined,
    fontWeight: el.weight || undefined,
    boxSizing: "border-box",
  };
  const fill = el.w || el.h || el.full ? "h-full w-full [&>*]:!h-full [&>*]:!w-full [&>*]:!max-w-none [&_img]:!h-full [&_img]:!w-full [&_img]:object-contain" : "";

  const start = (e, mode) => {
    e.stopPropagation(); e.preventDefault();
    const r = ref.current.getBoundingClientRect();
    drag.current = { mode, sx: e.clientX, sy: e.clientY, x0: el.x || 0, y0: el.y || 0, w0: el.w || r.width / scale, h0: el.h || r.height / scale, ratio: r.width / (r.height || 1), r0: r, z: (r.width / (ref.current.offsetWidth || r.width)) || 1 };
    const move = (ev) => {
      const d = drag.current; if (!d) return;
      const z = d.z || 1; const dxs = ev.clientX - d.sx, dys = ev.clientY - d.sy; const dx = dxs / z, dy = dys / z;
      const p = {};
      if (d.mode === "move") {
        p.x = Math.round(d.x0 + dx); p.y = Math.round(d.y0 + dy);
        // Smart guides: snap to container center/edges/quarters and to sibling centers/edges.
        const cont = ref.current.closest('[data-testid^="gl-block-"]') || ref.current.parentElement;
        const cr = cont.getBoundingClientRect(); const r0 = drag.current.r0;
        const left = r0.left + dxs, top = r0.top + dys, cx = left + r0.width / 2, cy = top + r0.height / 2;
        const vt = [[cr.left + cr.width / 2, "center"], [cr.left, "left"], [cr.right, "right"], [cr.left + cr.width / 4, "quarter"], [cr.left + cr.width * 3 / 4, "quarter"]];
        const ht = [[cr.top + cr.height / 2, "center"], [cr.top, "top"], [cr.bottom, "bottom"]];
        cont.querySelectorAll("[data-el]").forEach((n) => { if (n === ref.current || n.contains(ref.current) || ref.current.contains(n)) return; const b = n.getBoundingClientRect(); vt.push([b.left + b.width / 2, "sib"], [b.left, "sib"], [b.right, "sib"]); ht.push([b.top + b.height / 2, "sib"], [b.top, "sib"], [b.bottom, "sib"]); });
        const guides = []; const T = 6;
        for (const [gx, kind] of vt) { for (const [mine, adj] of [[cx, r0.width / 2], [left, 0], [left + r0.width, r0.width]]) { if (Math.abs(mine - gx) <= T) { p.x = Math.round(d.x0 + ((gx - adj) - r0.left) / z); guides.push({ axis: "v", pos: gx, kind }); break; } } if (guides.some((g) => g.axis === "v")) break; }
        for (const [gy, kind] of ht) { for (const [mine, adj] of [[cy, r0.height / 2], [top, 0], [top + r0.height, r0.height]]) { if (Math.abs(mine - gy) <= T) { p.y = Math.round(d.y0 + ((gy - adj) - r0.top) / z); guides.push({ axis: "h", pos: gy, kind }); break; } } if (guides.some((g) => g.axis === "h")) break; }
        ctx.onGuides?.(guides);
      }
      else {
        let w = d.w0, h = d.h0, x = d.x0, y = d.y0;
        if (d.mode.includes("e")) w = d.w0 + dx;
        if (d.mode.includes("w")) { w = d.w0 - dx; x = d.x0 + dx; }
        if (d.mode.includes("s")) h = d.h0 + dy;
        if (d.mode.includes("n")) { h = d.h0 - dy; y = d.y0 + dy; }
        if (kind === "image" && d.mode.length === 2 && !ev.shiftKey) h = w / d.ratio;
        p.w = Math.max(8, Math.round(w)); p.h = Math.max(8, Math.round(h)); p.x = Math.round(x); p.y = Math.round(y);
      }
      ctx.onChange?.(id, p);
    };
    const up = () => { drag.current = null; ctx.onGuides?.([]); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const editorProps = editor ? {
    onClickCapture: (e) => { if (e.target.closest("[data-el]") !== ref.current) return; e.stopPropagation(); e.preventDefault(); ctx.onSelect?.(id, { label: label || id, kind }); },
    onPointerDown: isSel ? (e) => { if (e.target.dataset.handle) return; start(e, "move"); } : undefined,
    title: label,
  } : {};

  return (
    <div ref={ref} data-el={id} data-testid={`el-${ctx.blockId}-${id}`} style={style} {...editorProps}
      className={`${className} ${editor ? "cursor-pointer" : ""} ${isSel ? "outline outline-2 outline-[#ec1e79] outline-offset-1" : editor ? "hover:outline hover:outline-1 hover:outline-dashed hover:outline-[#ec1e79]/70" : ""}`}>
      {fill ? <div className={fill}>{content}</div> : content}
      {isSel && (<>
        <span className="pointer-events-none absolute -top-5 left-0 z-40 whitespace-nowrap rounded bg-[#ec1e79] px-1.5 py-0.5 text-[10px] font-bold text-white">{label || id}</span>
        {HANDLES.map(([m, cls]) => <span key={m} data-handle={m} onPointerDown={(e) => start(e, m)} className={`absolute z-40 h-2.5 w-2.5 rounded-sm border border-white bg-[#ec1e79] ${cls}`} />)}
      </>)}
    </div>
  );
}
